import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpXsrfTokenExtractor } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AuthCheckService, ServicePathProviderService } from '@blk/cwp-ng-framework';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { LocalStorageKey } from '@ddt-web/generic-utils';
import { Observable, throwError } from 'rxjs';
import { catchError, first, map, switchMap, timeout } from 'rxjs/operators';

import { AuthUtil } from '../auth/auth.util';
import { SessionTimeoutService } from '../auth/session-timeout.service';

@Injectable()
export class InterceptorService implements HttpInterceptor {
	private readonly PUBLIC_APIS = ['/is-authenticated', '/login', 'model-families'];
	private readonly IGNORE_401_APIS = ['api/wealth/integrations']; // These are being handled separately by individual calls

	private isCwpUserSignedIn: boolean;

	constructor(
		private sessionTimeoutService: SessionTimeoutService,
		private servicePathProvider: ServicePathProviderService,
		private authService: AuthCheckService,
		private tokenExtractor: HttpXsrfTokenExtractor,
		@Inject('environment') private environment: any
	) { }

	public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const isPublicApiRequest = this.isPublicApiRequest(request);
		this.isCwpUserSignedIn = window?.['BLK']?.['isCwpUserSignedIn'] ?? false;

		if (isPublicApiRequest || AuthUtil.isOktaEnabled(this.environment.cloud)) {
			return this.handleRequest(next, request);
		} else {
			return this.authService.checkAuthorisation.pipe(
				first(check => check),
				switchMap(response => {
					return this.handleRequest(next, request);
				})
			);
		}

	}

	private handleRequest(next: HttpHandler, request: HttpRequest<any>): Observable<HttpEvent<any>> {

		const isApiGatewayRequest = request.url.includes(this.servicePathProvider.getServicePath('api-gateway'));
		if (isApiGatewayRequest) {
			request = this.interceptApiGatewayRequest(request);
		}

		return next.handle(request).pipe(
			map(response => {
				if (response instanceof HttpResponse) {
					if (this.isCwpUserSignedIn) {
						this.sessionTimeoutService.init();
					}
					return this.getValidResponse(response, request);
				}

				return response;
			}),
			timeout(request.url.includes('generateStream') ? SharedApplicationConstants.REPORT_GENERATION_REQUEST_TIMEOUT : SharedApplicationConstants.DEFAULT_REQUEST_TIMEOUT),
			catchError(error => {
				return this.getValidResponse(error, request);
			})
		);

	}

	private interceptApiGatewayRequest(request: HttpRequest<any>): HttpRequest<any> {
		const headers = {};

		// Add x-requester
		if (!request.headers.has('x-requester')) {
			headers['x-requester'] = 'AdvisorCenter';
		}

		// Add x-use-wds
		const isAdvisorCenterServerRequest = request.url.includes('/advisor-center/');
		if (isAdvisorCenterServerRequest) {
			if (!request.headers.has('x-use-wds')) {
				headers['x-use-wds'] = 'true';
			}
		}

		// Add CSRF Token
		const csrfToken = this.tokenExtractor.getToken();
		if (csrfToken !== null && !request.headers.has('X-NAUTILUS-XSRF-TOKEN')) {
			headers['X-NAUTILUS-XSRF-TOKEN'] = csrfToken;
		}

		request = request.clone({
			setHeaders: headers,
			withCredentials: true
		});
		return request;
	}

	private getValidResponse(response: HttpResponse<any>, request: HttpRequest<any>): any {
		if (response.status) {
			if (response.status >= 200 && response.status <= 300) {
				// "Workaround" to support fullName from CWP component
				const isGetUserRequest = request.url.includes('/get-user');
				if (isGetUserRequest && !response.body?.fullName) {
					let fullNameFallback = '';
					try {
						fullNameFallback = document.querySelector('blk-ac-header .user-name')?.textContent?.trim()
							|| JSON.parse(document.querySelector('blk-ac-header')?.attributes?.['header-data']?.value || '{}').profile?.userName;
					} catch (e) {
						console.error(e);
					}

					response = response.clone({
						body: {
							...response.body,
							fullName: fullNameFallback || response.body?.email
						}
					});
				}

				return response;
			}

			const shouldShowTimeoutDialog = (response.status === 401 || response.status === 403) && this.isCwpUserSignedIn;
			if (shouldShowTimeoutDialog) {
				const isIgnoredApi = this.IGNORE_401_APIS.some(apiUrl => request?.url?.includes(apiUrl));
				if (isIgnoredApi) {
					localStorage.removeItem(LocalStorageKey.ExternalPartner);
				} else {
					this.sessionTimeoutService.forceTimeout();
				}
			}

			return throwError(response);
		}

		return response;
	}

	private isPublicApiRequest(request: HttpRequest<any>): boolean {
		return this.PUBLIC_APIS.some(apiUrl => request.url.includes(apiUrl));
	}
}
